/**
 * Cibles — /targets.  Lot 5.
 *
 * Layout matches the onboarding "Qu'est-ce qu'on mesure ?" screens:
 *   - Pill tabs per category (colored active state)
 *   - Description text under the tabs
 *   - Table with identifying columns only
 *   - Inline edit per row ("Modifier" button)
 *   - Search bar above table
 *   - Import CSV + Voir le modèle at bottom
 */
import { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Trans, useTranslation } from 'react-i18next';
import { api, ApiError } from '../services/api';
import { useBrand } from '../contexts/BrandContext';
import {
  TARGET_LIMITS, type TargetCategory,
} from '@traaker/shared';

// ---------------------------------------------------------------------------
// Sub-module imports
// ---------------------------------------------------------------------------

import {
  type TargetRow, type TagRegistry,
  type CatConfig,
  tagRegistryOf, catCount,
  CATEGORIES, CAT_BY_ID,
} from './targets/CATEGORIES';
import {
  ImportError, prepareImportBody, parseCatalogueCsv, downloadCsv, gzipBytes,
} from './targets/csv';
import { useZoneAutocomplete } from './targets/useZoneAutocomplete';
import {
  ChipListEditor, GenericTargetDialog,
  ProductListView, ProductAddForm,
  CollectionListView, ContenuDomainView,
} from './targets/CatalogueViews';
import { EstablishmentAddForm, precisionLevel } from './targets/EstablishmentForm';
import { PersonneAddForm } from './targets/PersonneForm';
import { TargetTableRow } from './targets/TargetTableRow';
import { useTargetsData } from './targets/useTargetsData';

// ---------------------------------------------------------------------------
// Category pill tab
// ---------------------------------------------------------------------------

function CatTab({ cat, active, count, onClick }: { cat: CatConfig; active: boolean; count: number; onClick: () => void }) {
  const { Icon } = cat;
  const { t } = useTranslation();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1.5,
        py: 0.6,
        cursor: 'pointer',
        borderRadius: 10,
        border: '1.5px solid',
        borderColor: active ? cat.color : alpha('#000', 0.14),
        bgcolor: active ? cat.color : 'transparent',
        color: active ? '#fff' : 'text.secondary',
        fontWeight: active ? 600 : 400,
        fontSize: '0.8125rem',
        transition: 'all 0.12s',
        '&:hover': { borderColor: cat.color, color: active ? '#fff' : cat.color },
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon sx={{ fontSize: 14, opacity: active ? 1 : 0.6 }} />
      <span>{t(cat.labelKey)}</span>
      {count > 0 && (
        <Box
          sx={{
            ml: 0.25,
            minWidth: 17,
            height: 17,
            borderRadius: 10,
            bgcolor: active ? 'rgba(255,255,255,0.25)' : alpha(cat.color, 0.15),
            color: active ? '#fff' : cat.color,
            fontSize: '0.65rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0.4,
            lineHeight: 1,
          }}
        >
          {count > 99 ? '99+' : count}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SitemapImportDialog — sitemap-based bulk import for produit/collection (2026-09-23)
// ---------------------------------------------------------------------------

/**
 * Two-step dialog:
 *   1. Domain → detect sitemaps → pick ONE sitemap → configure type/brand/tags
 *   2. On "Importer": harvest URLs → build NDJSON → staged-import (begin/PUT/commit)
 *
 * Uses the staged NDJSON path so 50k-URL imports don't hit the batch POST 409 loop.
 * Server-side merge (`_carry_over_produit_collection`) reuses existing targetIds.
 */
function SitemapImportDialog({
  open,
  brandId,
  marques,
  importLocked,
  onClose,
  onSuccess,
}: {
  open: boolean;
  brandId: string;
  marques: TargetRow[];
  importLocked: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [sitemapUrls, setSitemapUrls] = useState<string[] | null>(null);
  const [wafBlocked, setWafBlocked] = useState(false);
  const [selectedSitemap, setSelectedSitemap] = useState('');
  const [type, setType] = useState<'produit' | 'collection'>('produit');
  const [parentTargetId, setParentTargetId] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const reset = () => {
    setDomain(''); setDetecting(false); setDetectError(null);
    setSitemapUrls(null); setWafBlocked(false); setSelectedSitemap('');
    setType('produit'); setParentTargetId(''); setTags([]);
    setImporting(false); setImportError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleDetect = async () => {
    const d = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '').trim();
    if (!d) return;
    setDetecting(true); setDetectError(null); setSitemapUrls(null); setWafBlocked(false); setSelectedSitemap('');
    try {
      const res = await api.post(`/brands/${brandId}/targets/sitemap/discover`, { domain: d }) as {
        sitemapUrls: string[]; wafBlocked: boolean;
      };
      setSitemapUrls(res.sitemapUrls || []);
      setWafBlocked(!!res.wafBlocked);
      if (res.sitemapUrls?.length) setSelectedSitemap(res.sitemapUrls[0]);
    } catch (e: any) {
      setDetectError(e?.message ?? t('cibles.errors.generic'));
    } finally { setDetecting(false); }
  };

  const handleImport = async () => {
    if (!selectedSitemap) { setImportError(t('cibles.sitemap.error_no_sitemap')); return; }
    setImporting(true); setImportError(null);
    try {
      // Harvest URLs from the chosen sitemap.
      const res = await api.post(`/brands/${brandId}/targets/sitemap/harvest`, { sitemapUrl: selectedSitemap }) as {
        urls: Array<{ url: string; name?: string; id: string }>;
        total: number;
        truncated?: boolean;
      };
      if (!res.urls?.length) { setImportError(t('cibles.sitemap.no_sitemaps')); return; }

      // Build NDJSON: one row per URL.
      const rows = res.urls.map((u) => {
        const fields: Array<{ name: string; value: string }> = [{ name: 'id', value: u.id }];
        if (type === 'produit') {
          if (u.name) fields.push({ name: 'name', value: u.name });
          fields.push({ name: 'url', value: u.url });
        } else {
          fields.push({ name: 'url', value: u.url });
          if (u.name) fields.push({ name: 'name', value: u.name });
        }
        const row: Record<string, unknown> = {
          category: type,
          label: u.name || u.url,
          fields,
        };
        if (parentTargetId) row.parentTargetId = parentTargetId;
        if (tags.length) row._raw_tags = tags;
        return JSON.stringify(row);
      });
      const ndjsonBytes = new TextEncoder().encode(rows.join('\n'));
      const uploadBody = await gzipBytes(ndjsonBytes);

      // Staged import: begin → PUT → commit.
      const begun = await api.post(`/brands/${brandId}/targets/import`, {
        action: 'begin',
        declaredBytes: uploadBody.byteLength,
      }) as { importId: string; uploadUrl: string };

      const putResp = await fetch(begun.uploadUrl, {
        method: 'PUT',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: uploadBody as any,
        headers: { 'Content-Type': 'application/x-ndjson' },
      });
      if (!putResp.ok) throw new Error(t('cibles.errors.upload', { status: putResp.status }));

      await api.post(`/brands/${brandId}/targets/import`, { action: 'commit', importId: begun.importId });

      if (res.truncated) alert(t('cibles.sitemap.truncated'));
      onSuccess();
      handleClose();
    } catch (e: any) {
      setImportError(e?.message ?? t('cibles.sitemap.error_harvest'));
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, pt: 2.5, pb: 0 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1, fontSize: '1rem' }}>
          {t('cibles.sitemap.dialog_title')}
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 2 }}>

        {/* Domain input + detect */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label={t('cibles.sitemap.domain_label')}
            placeholder={t('cibles.sitemap.domain_placeholder')}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleDetect(); }}
            disabled={detecting || importing}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleDetect}
            disabled={!domain.trim() || detecting || importing}
            sx={{ whiteSpace: 'nowrap', minWidth: 'max-content', fontSize: '0.8125rem' }}
          >
            {detecting ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> : null}
            {t(detecting ? 'cibles.sitemap.detecting' : 'cibles.sitemap.detect_button')}
          </Button>
        </Box>

        {detectError && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8125rem' }}>{detectError}</Alert>}

        {sitemapUrls !== null && (
          <>
            {wafBlocked && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8125rem' }}>
                {t('cibles.sitemap.waf_alert')}
              </Alert>
            )}
            {sitemapUrls.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8125rem' }}>
                {t('cibles.sitemap.no_sitemaps')}
              </Alert>
            ) : (
              <>
                {/* Sitemap list — radio (one selection) */}
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('cibles.sitemap.select_label')}
                </Typography>
                <Box sx={{ mb: 2, maxHeight: 160, overflowY: 'auto', border: '1px solid', borderColor: alpha('#000', 0.12), borderRadius: 1 }}>
                  {sitemapUrls.map((u) => (
                    <Box
                      key={u}
                      onClick={() => setSelectedSitemap(u)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
                        cursor: 'pointer', bgcolor: selectedSitemap === u ? alpha('#7C3AED', 0.06) : 'transparent',
                        '&:hover': { bgcolor: alpha('#000', 0.04) },
                        borderBottom: '1px solid', borderColor: alpha('#000', 0.06),
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Box sx={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                        border: '2px solid', borderColor: selectedSitemap === u ? '#7C3AED' : alpha('#000', 0.3),
                        bgcolor: selectedSitemap === u ? '#7C3AED' : 'transparent',
                      }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{u}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Type radio */}
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('cibles.sitemap.type_label')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {(['produit', 'collection'] as const).map((v) => (
                    <Box
                      key={v}
                      onClick={() => setType(v)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75,
                        border: '1px solid', borderRadius: 1, cursor: 'pointer',
                        borderColor: type === v ? '#7C3AED' : alpha('#000', 0.18),
                        bgcolor: type === v ? alpha('#7C3AED', 0.06) : 'transparent',
                      }}
                    >
                      <Box sx={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                        border: '2px solid', borderColor: type === v ? '#7C3AED' : alpha('#000', 0.3),
                        bgcolor: type === v ? '#7C3AED' : 'transparent',
                      }} />
                      <Typography variant="caption" sx={{ fontSize: '0.8125rem' }}>
                        {t(v === 'produit' ? 'cibles.sitemap.type_product' : 'cibles.sitemap.type_collection')}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Parent brand (optional) */}
                {marques.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('cibles.sitemap.brand_label')}
                    </Typography>
                    <Select
                      size="small"
                      fullWidth
                      value={parentTargetId}
                      onChange={(e) => setParentTargetId(e.target.value)}
                      displayEmpty
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <MenuItem value=""><em>{t('cibles.sitemap.brand_none')}</em></MenuItem>
                      {marques.map((m) => (
                        <MenuItem key={m.targetId} value={m.targetId}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}

                {/* Tags */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('cibles.sitemap.tags_label')}
                  </Typography>
                  <ChipListEditor
                    values={tags}
                    onChange={setTags}
                    max={TARGET_LIMITS.maxTagsPerTarget}
                    maxLength={TARGET_LIMITS.maxTagLength}
                    placeholder="tag…"
                    hex="#7C3AED"
                    disabled={importing}
                  />
                </Box>

                {importError && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8125rem' }}>{importError}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button size="small" variant="text" onClick={handleClose} disabled={importing}
                    sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                    {t('cibles.actions.cancel')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleImport}
                    disabled={!selectedSitemap || importing || importLocked}
                    sx={{ fontSize: '0.8125rem', bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}
                  >
                    {importing ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> : null}
                    {t(importing ? 'cibles.sitemap.importing' : 'cibles.sitemap.import_button')}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Panel — embedded in the GeoStrategy Cibles card
// ---------------------------------------------------------------------------

/**
 * Core Cibles UI. Embedded only, inside the GeoStrategy Cibles card (D27) —
 * the standalone `/targets` page was removed once the card became the single
 * entry point, along with the `compact` prop that switched between the two.
 */
export function TargetsPanel() {
  const { activeBrand } = useBrand();
  const { t, i18n } = useTranslation(); // i18n.language also picks the CSV template locale

  const [activeTab, setActiveTab] = useState<TargetCategory>('marque');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Non-failure feedback (e.g. a commit replay) — rendered as an info Alert, not an error. */
  const [notice, setNotice] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [productEditRow, setProductEditRow] = useState<TargetRow | null>(null);
  const [contenuEditRow, setContenuEditRow] = useState<TargetRow | null>(null);
  const [collectionEditRow, setCollectionEditRow] = useState<TargetRow | null>(null);
  const [sitemapDialogOpen, setSitemapDialogOpen] = useState(false);

  // Search
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<TargetRow[]>([]);

  /**
   * Tag filter — IDS, because that is what the rows and the API speak; the chips resolve
   * through `tagRegistry`. Server-side (see `loadPage`), so it resets pagination like a tab
   * switch does rather than filtering the page in place.
   */
  const [tagFilter, setTagFilter] = useState<number[]>([]);
  const [tagMenuAnchor, setTagMenuAnchor] = useState<HTMLElement | null>(null);

  const brandId = activeBrand?.brandId ?? '';

  // Reset UI state on tab / brand / tag-filter change — mirrors the original
  // `useEffect` that was in TargetsPanel before data loading was extracted.
  useEffect(() => {
    setShowAdd(false); setSearchMode(false); setSearchQ('');
  }, [brandId, activeTab, tagFilter]);

  // Data fetching hook
  const {
    manifest, rows, setRows, marques, loading,
    page, pageCursors, hasNextPage,
    loadSummary, loadPage, loadRows, loadMarques,
  } = useTargetsData({
    brandId,
    activeBrand,
    activeTab,
    tagFilter,
    setError,
  });

  // Rebuilt whenever the manifest is re-read, which is also how a RENAMED tag reaches the UI:
  // the ids on the rows never change, so nothing else has to be refetched.
  const tagRegistry: TagRegistry = tagRegistryOf(manifest);

  // Zone autocomplete (shared state for one open field at a time)
  const zoneHook = useZoneAutocomplete(1000);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const cat = CAT_BY_ID[activeTab];

  const zoneState = {
    query: zoneHook.query,
    suggestions: zoneHook.suggestions,
    attribution: zoneHook.attribution,
    selected: zoneHook.selected,
    onQueryChange: zoneHook.onQueryChange,
    onSelect: zoneHook.onSelect,
  };

  // ── Actions ──

  // One error surface for every Cibles WRITE. Two statuses need more than a message:
  //
  //   504 — the API stopped WAITING for targets-consolidate; the Lambda itself was NOT
  //         cancelled (aborting a synchronous Invoke never stops the function), so the write
  //         is most likely landing right now. Retrying is the one thing that must not happen:
  //         it queues a SECOND full-partition rewrite. Shown as an info notice, not an error,
  //         and followed by a refresh so the lock/counters the run is producing become
  //         visible — a red "échec" here is what makes a user retry a write that succeeded.
  //   409 TARGETS_CONFLICT — the worker hit the import/reshard lock inside its own CAS loop,
  //         so another write is in flight. Nothing was persisted; transient by construction.
  //         Refresh the summary so the lock that caused it shows up and the controls disable
  //         themselves instead of inviting an immediate retry.
  //
  // Both carry a French `message` from the API (targets.service.ts), so it is displayed
  // verbatim; the local keys are only the fallback for a body that has none.
  const reportWriteError = (e: any, fallbackKey = 'cibles.errors.generic') => {
    if (e instanceof ApiError && e.status === 504) {
      setNotice(e.message || t('cibles.errors.timeout'));
      loadRows(); loadSummary();
      return;
    }
    if (e instanceof ApiError && e.status === 409 && e.body?.code === 'TARGETS_CONFLICT') {
      setError(e.message || t('cibles.errors.conflict'));
      loadSummary();
      return;
    }
    setError(e?.message ?? t(fallbackKey));
  };

  const handleDelete = async (targetId: string, ownerId?: string, category?: string) => {
    try {
      const params = new URLSearchParams();
      if (ownerId) params.set('ownerId', ownerId);
      if (category) params.set('category', category);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await api.delete(`/brands/${brandId}/targets/${targetId}${qs}`) as any;
      if (resp?.status === 'queued') {
        // Large partition: the API queued the delete under the job lock instead of running
        // it in the request. The row is still there until the worker flips; the banner and
        // the poll take over from here.
        setNotice(t('cibles.job.queued_async'));
        loadSummary();
        return;
      }
      setRows((prev) => prev.filter((r) => r.targetId !== targetId));
      loadSummary();
    } catch (e: any) { reportWriteError(e); }
  };

  const handleAdd = async (data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[] }) => {
    setSaving(true);
    try {
      const resp = await api.post(`/brands/${brandId}/targets`, { targets: [{ ownerId: 'own', category: activeTab, ...data }] }) as any;
      if (resp?.status === 'queued') setNotice(t('cibles.job.queued_async'));
      setShowAdd(false); zoneHook.reset();
      loadRows(); loadSummary();
      if (activeTab === 'marque') loadMarques();
    } catch (e: any) { reportWriteError(e); } finally { setSaving(false); }
  };

  const handleSave = async (targetId: string, data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[] }) => {
    // Send the existing targetId so Python removes old stale signal rows for this
    // target before writing the new ones (prevents orphan signal accumulation).
    // Do NOT delete+recreate — that assigns a new UUID and leaves old signals forever.
    //
    // Report AND re-throw: the dialog's onSave re-throws on failure so it stays open,
    // giving the user the error context rather than silently closing.
    try {
      const resp = await api.post(`/brands/${brandId}/targets`, {
        targets: [{ targetId, ownerId: 'own', category: activeTab, ...data }],
      }) as any;
      if (resp?.status === 'queued') setNotice(t('cibles.job.queued_async'));
      loadRows(); loadSummary();
      if (activeTab === 'marque') loadMarques();
      return resp;
    } catch (e: any) {
      reportWriteError(e);
      throw e;
    }
  };

  // Search — explicit (Enter / the Chercher button), never per keystroke, so the
  // `targets_search` budget (600/h) is not a concern for a normal user. A 429 is still
  // possible on repeated submits: keep whatever is already on screen rather than
  // clearing the list or raising an error for something the user will simply retry.
  const handleSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true); setSearchMode(true);
    try {
      const data = await api.get(`/brands/${brandId}/targets/search?q=${encodeURIComponent(searchQ)}&category=${activeTab}`) as any;
      setSearchResults(data.results ?? []);
    } catch (e: any) {
      if (!(e instanceof ApiError && e.status === 429)) setError(e?.message ?? t('cibles.errors.generic'));
    } finally { setSearching(false); }
  };

  const clearSearch = () => { setSearchMode(false); setSearchQ(''); setSearchResults([]); };

  // Contenus bulk save.
  //
  // Import
  const handleImport = async (file: File) => {
    setSaving(true);
    try {
      // ── Produit: merge-aware direct POST (avoids duplicates on re-import) ──────
      // The bulk NDJSON import path mints a fresh targetId for every row, so
      // re-importing the same CSV would create duplicate Target rows for every
      // product.  Instead: parse the CSV, load existing produit targetIds, include
      // them in the POST so apply_delta reuses the existing row and only ADDS new
      // URL signals — existing ones are preserved by apply_delta's merge semantics.
      if (activeTab === 'produit' || activeTab === 'collection') {
        const { produits, collections, errors, warnings } = await parseCatalogueCsv(file, marques);
        if (errors.length) {
          const shown = errors.slice(0, 10);
          const extra = errors.length > 10 ? `\n… et ${errors.length - 10} autre(s) erreur(s)` : '';
          throw new ImportError(shown.join('\n') + extra);
        }

        /** Load existing targets of `category` and return id_field → targetId map. */
        const loadExistingById = async (category: string): Promise<Map<string, string>> => {
          const map = new Map<string, string>();
          let cursor: string | null = null;
          for (let page = 0; page < 5; page++) {
            const params = new URLSearchParams({ category, limit: '200' });
            if (cursor) params.set('cursor', cursor);
            const data = await api.get(`/brands/${brandId}/targets?${params}`) as { items: TargetRow[]; nextCursor: string | null };
            for (const row of data.items) {
              const idField = row.fields.find((f) => f.name === 'id')?.value as string | undefined;
              if (idField) map.set(idField, row.targetId);
              // For collection: also index by URL as fallback merge key
              if (category === 'collection') {
                const urlField = row.fields.find((f) => f.name === 'url')?.value as string | undefined;
                if (urlField && !idField) map.set(urlField, row.targetId);
              }
            }
            if (!data.nextCursor) break;
            cursor = data.nextCursor;
          }
          return map;
        };

        // The API requires each POST batch to target a single (ownerId, category) partition.
        // produit and collection must therefore be sent as separate requests — never mixed.
        const produitTargets: any[] = [];
        const collectionTargets: any[] = [];

        if (produits.length) {
          const existingProduits = await loadExistingById('produit');
          for (const g of produits) {
            const existingTid = existingProduits.get(g.id);
            const target: any = {
              ownerId: 'own', category: 'produit',
              label: g.name || g.id,
              parentTargetId: g.parentTargetId,
              fields: [
                { name: 'id', value: g.id },
                ...(g.name ? [{ name: 'name', value: g.name }] : []),
                ...g.urls.map((u) => ({ name: 'url', value: u.url, ...(u.buyingPage ? { buyingPage: true } : {}) })),
              ],
              tags: g.tags,
            };
            if (existingTid) target.targetId = existingTid;
            produitTargets.push(target);
          }
        }

        if (collections.length) {
          const existingCollections = await loadExistingById('collection');
          for (const g of collections) {
            const mergeKey = g.id || g.url;
            const existingTid = existingCollections.get(mergeKey);
            const fields: any[] = [];
            if (g.id) fields.push({ name: 'id', value: g.id });
            fields.push({ name: 'url', value: g.url });
            if (g.name) fields.push({ name: 'name', value: g.name });
            const target: any = {
              ownerId: 'own', category: 'collection',
              label: g.name || g.url,
              parentTargetId: g.parentTargetId,
              fields,
              tags: g.tags,
            };
            if (existingTid) target.targetId = existingTid;
            collectionTargets.push(target);
          }
        }

        // POST produits and collections as separate batches — the API partitions by
        // (ownerId, category) and refuses mixed-category requests.
        for (let i = 0; i < produitTargets.length; i += TARGET_LIMITS.maxBatchRows) {
          const resp = await api.post(`/brands/${brandId}/targets`, {
            targets: produitTargets.slice(i, i + TARGET_LIMITS.maxBatchRows),
          }) as any;
          if (resp?.status === 'queued') setNotice(t('cibles.job.queued_async'));
        }
        for (let i = 0; i < collectionTargets.length; i += TARGET_LIMITS.maxBatchRows) {
          const resp = await api.post(`/brands/${brandId}/targets`, {
            targets: collectionTargets.slice(i, i + TARGET_LIMITS.maxBatchRows),
          }) as any;
          if (resp?.status === 'queued') setNotice(t('cibles.job.queued_async'));
        }
        if (warnings.length) setNotice(warnings.slice(0, 5).join('\n') + (warnings.length > 5 ? `\n… et ${warnings.length - 5} autre(s)` : ''));
        loadRows(); loadSummary();
        return;
      }

      // ── Other categories: standard NDJSON bulk import ─────────────────────
      // Always gzipped bytes — never the raw File (see prepareImportBody).
      // marques needed by all categories that support a parentTargetId (contenu,
      // etablissement, personne) so marque_label → parentTargetId resolution works.
      const { body: uploadBody, warnings: importWarnings } = await prepareImportBody(
        file, activeTab, marques,
      );

      // `declaredBytes` is the exact gzipped length, which the API signs into the
      // presigned PUT as ContentLength. Without it the URL is unpinned: anything up to
      // the worker's own cap can be staged under it for the whole TTL.
      let begun: { importId: string; uploadUrl: string; maxBytes?: number };
      try {
        begun = await api.post(`/brands/${brandId}/targets/import`, {
          action: 'begin',
          declaredBytes: uploadBody.byteLength,
        });
      } catch (e: any) {
        // 413 = declaredBytes over the staging cap. Our own copy, not the API's.
        if (e instanceof ApiError && e.status === 413) throw new ImportError('cibles.import.error_too_large');
        throw e;
      }
      const { importId, uploadUrl, maxBytes } = begun;
      // Belt and braces: reject before burning the presigned PUT rather than letting S3
      // fail the upload and leave the staging slot dangling.
      if (maxBytes && uploadBody.byteLength > maxBytes) {
        await api.post(`/brands/${brandId}/targets/import`, { action: 'abort', importId }).catch(() => { /* best effort */ });
        throw new ImportError('cibles.import.error_too_large');
      }

      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        body: uploadBody,
        headers: { 'Content-Type': 'application/x-ndjson' },
      });
      if (importWarnings.length) {
        setNotice(importWarnings.slice(0, 5).join('\n') + (importWarnings.length > 5 ? `\n… et ${importWarnings.length - 5} autre(s)` : ''));
      }
      if (!putResp.ok) throw new Error(t('cibles.errors.upload', { status: putResp.status }));
      try {
        await api.post(`/brands/${brandId}/targets/import`, { action: 'commit', importId });
      } catch (e: any) {
        // A commit replay (double-click) is refused with 409 IMPORT_ALREADY_COMMITTED.
        // The import IS running, so this is information, not a failure.
        if (e instanceof ApiError && e.status === 409 && e.body?.code === 'IMPORT_ALREADY_COMMITTED') {
          setNotice(t('cibles.import.already_committed'));
        } else throw e;
      }
      loadSummary();
    } catch (e: any) {
      if (e instanceof ImportError) setError(t(e.key));
      else reportWriteError(e, 'cibles.errors.import');
    } finally { setSaving(false); }
  };

  // Locks
  const importLocked = !!manifest?.importJob;
  const geocodeLocked = !!manifest?.geocodeJob;
  const tabLocked = activeTab === 'etablissement' ? (importLocked || geocodeLocked) : importLocked;

  const displayRows = searchMode ? searchResults : rows;
  const totalCount = CATEGORIES.reduce((s, c) => s + catCount(manifest, c.id), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>

      {/* ── Card header ── the counts read off `manifest`, not the brand row's
          cached `targetsMeta`, so they stay correct after an in-place edit. */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>{t('cibles.title')}</Typography>
            {/* Description — always visible; helps new users understand the purpose at a glance */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8125rem', lineHeight: 1.55 }}>
              {t('cibles.explainer')}
            </Typography>
          </Box>
          <Tooltip title={<Box sx={{ p: 0.5, maxWidth: 270 }}><Typography variant="caption" fontWeight={700} display="block" gutterBottom>{t('cibles.determinism_title')}</Typography><Typography variant="caption">{t('cibles.determinism')}</Typography></Box>} placement="bottom-end" arrow>
            <IconButton size="small" sx={{ mt: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <InfoOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Signal progress bar — full card width ── */}
      {(() => {
        const signalCount = manifest?.signalCount ?? 0;
        const maxSignals = 500_000;
        const pct = Math.min(100, (signalCount / maxSignals) * 100);
        const color = pct >= 95 ? '#EF4444' : pct >= 80 ? '#F59E0B' : '#10B981';
        return (
          <Box sx={{ px: 0, borderBottom: '1px solid', borderColor: alpha('#000', 0.07), pb: 0, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, pb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', flexShrink: 0 }}>
                {t('cibles.signals_cap')}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip
                title={
                  <Box sx={{ p: 0.5, maxWidth: 280 }}>
                    <Typography variant="caption" fontWeight={700} display="block" gutterBottom>{t('cibles.glossary.signals_title')}</Typography>
                    <Typography variant="caption" display="block">{t('cibles.glossary.signals_body')}</Typography>
                  </Box>
                }
                placement="bottom-end"
                arrow
              >
                <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color, cursor: 'help', flexShrink: 0 }}>
                  {signalCount.toLocaleString(i18n.language)} / {maxSignals.toLocaleString(i18n.language)}
                </Typography>
              </Tooltip>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 3,
                borderRadius: 0,
                bgcolor: alpha(color, 0.12),
                '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 0 },
              }}
            />
          </Box>
        );
      })()}

      {/* ── Job banners ── */}
      {importLocked && (
        <Box sx={{ px: 2.5, py: 0.875, bgcolor: alpha('#0EA5E9', 0.07), borderBottom: '1px solid', borderColor: alpha('#0EA5E9', 0.18), display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <HourglassBottomIcon sx={{ fontSize: 13, color: '#0284C7' }} />
          <Box sx={{ flex: 1 }}><Typography variant="caption" fontWeight={600} color="#0284C7">{manifest?.importJob?.kind === 'delta' || manifest?.importJob?.kind === 'delete'
            ? t('cibles.job.editing')
            : manifest?.importJob?.status === 'queued' ? t('cibles.import.queued') : t('cibles.job.processing')}</Typography><LinearProgress sx={{ mt: 0.5, height: 2, borderRadius: 1 }} /></Box>
        </Box>
      )}
      {geocodeLocked && (
        <Box sx={{ px: 2.5, py: 0.875, bgcolor: alpha('#F59E0B', 0.07), borderBottom: '1px solid', borderColor: alpha('#F59E0B', 0.18), display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <HourglassBottomIcon sx={{ fontSize: 13, color: '#D97706' }} />
          <Box sx={{ flex: 1 }}><Typography variant="caption" fontWeight={600} color="#B45309">{t('cibles.job.geocoding', { resolved: manifest?.geocodeJob?.resolved ?? 0, rows: manifest?.geocodeJob?.rows ?? 0 })}</Typography><LinearProgress variant="determinate" value={Math.round(((manifest?.geocodeJob?.resolved ?? 0) / (manifest?.geocodeJob?.rows || 1)) * 100)} sx={{ mt: 0.5, height: 2, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#D97706' } }} /></Box>
        </Box>
      )}

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2.5, mt: 1, borderRadius: 1.5, fontSize: '0.8125rem', flexShrink: 0 }}>{error}</Alert>}
      {notice && <Alert severity="info" onClose={() => setNotice(null)} sx={{ mx: 2.5, mt: 1, borderRadius: 1.5, fontSize: '0.8125rem', flexShrink: 0 }}>{notice}</Alert>}

      {/* ── Tabs + description ── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Typography variant="caption" fontWeight={600} color="text.disabled" sx={{ display: 'block', mb: 1, fontSize: '0.68rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {t('cibles.section.measured')}
        </Typography>
        {/* ── Main tab row — all pills aligned ── */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75, alignItems: 'center' }}>
          {CATEGORIES.filter((c) => c.id !== 'collection').map((c) => {
            if (c.id === 'produit') {
              // "Catalogue" pill represents both produit + collection
              const catalogueCount = catCount(manifest, 'produit') + catCount(manifest, 'collection');
              const catalogueActive = activeTab === 'produit' || activeTab === 'collection';
              const catalogueCat = { ...c, labelKey: 'cibles.tab.catalogue' };
              return (
                <CatTab
                  key="catalogue"
                  cat={catalogueCat}
                  active={catalogueActive}
                  count={catalogueCount}
                  onClick={() => setActiveTab(activeTab === 'collection' ? 'collection' : 'produit')}
                />
              );
            }
            return (
              <CatTab key={c.id} cat={c} active={activeTab === c.id} count={catCount(manifest, c.id)} onClick={() => setActiveTab(c.id)} />
            );
          })}
        </Box>

        {/* ── Sub-tabs for Catalogue (visible only when catalogue is active) ── */}
        {(activeTab === 'produit' || activeTab === 'collection') && (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.25, pl: 0.25 }}>
            {(['produit', 'collection'] as const).map((sub) => {
              const subCat = CAT_BY_ID[sub];
              const isActive = activeTab === sub;
              return (
                <Box
                  key={sub}
                  onClick={() => setActiveTab(sub)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.25,
                    py: 0.35,
                    cursor: 'pointer',
                    borderRadius: 10,
                    fontSize: '0.75rem',
                    fontWeight: isActive ? 700 : 400,
                    color: isActive ? '#0D9488' : 'text.secondary',
                    bgcolor: isActive ? alpha('#0D9488', 0.1) : 'transparent',
                    border: '1px solid',
                    borderColor: isActive ? alpha('#0D9488', 0.35) : alpha('#000', 0.1),
                    transition: 'all 0.12s',
                    '&:hover': { bgcolor: alpha('#0D9488', isActive ? 0.1 : 0.05), borderColor: alpha('#0D9488', 0.3) },
                  }}
                >
                  <subCat.Icon sx={{ fontSize: 13 }} />
                  <span>{t(subCat.labelKey)}</span>
                  {catCount(manifest, sub) > 0 && (
                    <Typography component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, color: isActive ? '#0D9488' : 'text.disabled', ml: 0.25 }}>
                      {catCount(manifest, sub) > 999 ? `${Math.round(catCount(manifest, sub) / 1000)}k` : catCount(manifest, sub)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
          {t(cat.descKey)}
        </Typography>
      </Box>

      {/* ── Search bar ── */}
      <Box sx={{ px: 2.5, pb: 1.25, flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder={t('cibles.search.placeholder', { cat: t(cat.labelKey).toLowerCase() })}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
            endAdornment: searchMode ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end">
                <Button size="small" variant="text" onClick={handleSearch} disabled={searching || !searchQ.trim()} sx={{ fontSize: '0.72rem', minWidth: 0, color: cat.color }}>
                  {searching ? <CircularProgress size={12} /> : t('cibles.search.button')}
                </Button>
              </InputAdornment>
            ),
            sx: { fontSize: '0.8125rem', bgcolor: alpha('#000', 0.025), '& fieldset': { border: 'none' }, borderRadius: 2 },
          }}
        />
        {searchMode && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.72rem' }}>
            {t('cibles.search.results', { count: searchResults.length })} · <Button size="small" variant="text" onClick={clearSearch} sx={{ fontSize: '0.72rem', p: 0, color: 'text.secondary', textDecoration: 'underline' }}>{t('cibles.search.see_all')}</Button>
          </Typography>
        )}

        {/* Filtrer par tag.
            Hidden entirely when the brand has defined no tag: an empty menu is worse than no
            button, and the registry is brand-wide so this is the same test on every tab. */}
        {tagRegistry.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
            <Box
              onClick={(e: React.MouseEvent) => setTagMenuAnchor(e.currentTarget as HTMLElement)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: 600, px: 1, py: 0.35, borderRadius: 10,
                border: '1px solid',
                borderColor: tagFilter.length ? alpha('#2563EB', 0.4) : alpha('#000', 0.12),
                color: tagFilter.length ? '#1E40AF' : 'text.secondary',
                bgcolor: tagFilter.length ? alpha('#2563EB', 0.07) : 'transparent',
                '&:hover': { borderColor: alpha('#2563EB', 0.35), color: '#1E40AF' },
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 12 }} />
              {t('cibles.tags.filter')}
              {tagFilter.length > 0 && (
                <Box component="span" sx={{ fontWeight: 700 }}>· {tagFilter.length}</Box>
              )}
            </Box>
            {/* The active tags are echoed OUTSIDE the menu: with the menu shut, a filtered
                list is otherwise indistinguishable from a brand that has few cibles. */}
            {tagFilter.map((id) => {
              const name = tagRegistry.get(id);
              if (!name) return null;
              return (
                <Box
                  key={id}
                  onClick={() => setTagFilter((prev) => prev.filter((x) => x !== id))}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer',
                    bgcolor: alpha('#2563EB', 0.08), color: '#1E40AF',
                    fontSize: '0.7rem', fontWeight: 600, pl: 0.875, pr: 0.5, py: 0.25, borderRadius: 0.75,
                  }}
                >
                  {name}
                  <CloseIcon sx={{ fontSize: 11 }} />
                </Box>
              );
            })}
            {tagFilter.length > 0 && (
              <Button size="small" variant="text" onClick={() => setTagFilter([])}
                sx={{ fontSize: '0.7rem', p: 0, minWidth: 0, color: 'text.secondary', textDecoration: 'underline' }}>
                {t('cibles.tags.filter_clear')}
              </Button>
            )}
            <Popover
              open={!!tagMenuAnchor}
              anchorEl={tagMenuAnchor}
              onClose={() => setTagMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{ sx: { boxShadow: '0 4px 16px rgba(0,0,0,.1)', borderRadius: 1.5, minWidth: 210, maxHeight: 320 } }}
            >
              <Box sx={{ py: 0.5 }}>
                {[...tagRegistry.entries()].map(([id, name]) => {
                  const on = tagFilter.includes(id);
                  return (
                    <MenuItem
                      key={id}
                      // Multi-select, and the menu stays open: filtering by two tags is one
                      // thought, not two trips through the menu.
                      onClick={() => setTagFilter((prev) => (on ? prev.filter((x) => x !== id) : [...prev, id]))}
                      sx={{ fontSize: '0.8125rem', gap: 1, py: 0.6, fontWeight: on ? 600 : 400, color: on ? '#1E40AF' : 'text.primary' }}
                    >
                      <Box sx={{
                        width: 13, height: 13, borderRadius: 0.5, flexShrink: 0,
                        border: '1.5px solid', borderColor: on ? '#2563EB' : alpha('#000', 0.25),
                        bgcolor: on ? '#2563EB' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                      }}>
                        {on && <CheckCircleOutlineIcon sx={{ fontSize: 10 }} />}
                      </Box>
                      {name}
                    </MenuItem>
                  );
                })}
              </Box>
            </Popover>
          </Box>
        )}
      </Box>

      {/* ── Table ── */}
      <Box sx={{ overflowY: 'visible' }}>

        <>
            {/* Amber precision banner — établissement only */}
            {activeTab === 'etablissement' && (() => {
              const imprecise = rows.filter((r) => precisionLevel(r) < 3);
              if (imprecise.length === 0) return null;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.25, bgcolor: '#FBF3E4', borderBottom: '1px solid #F0E4CC' }}>
                  <Box sx={{ flexShrink: 0, width: 28, height: 28, borderRadius: 1, bgcolor: '#F3E2C4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7A4A10', fontSize: 14 }}>
                    ★
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#7A4A10', display: 'block', fontSize: '0.8rem' }}>
                      {t('cibles.precision.banner', { count: imprecise.length })}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#7C5A1E' }}>
                      {t('cibles.precision.banner_detail')}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}

            {/* Column headers — not shown for produit/contenu/collection (custom layouts) */}
            {displayRows.length > 0 && activeTab !== 'contenu' && activeTab !== 'produit' && activeTab !== 'collection' && (
              <Box sx={{ display: 'flex', alignItems: 'center', px: 0, py: 0.75, bgcolor: alpha('#000', 0.025), borderBottom: '1px solid', borderColor: alpha('#000', 0.07) }}>
                {cat.hasParent && (
                  <Typography variant="caption" sx={{ width: 140, flexShrink: 0, px: 1.5, fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                    {t('cibles.parent_column')}
                  </Typography>
                )}
                {cat.id === 'etablissement' ? (
                  <>
                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0, px: 3, fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                      {t('cibles.tab.etablissement')}
                    </Typography>
                    <Typography variant="caption" sx={{ width: 300, flexShrink: 0, px: 0, pr: 3, fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                      {t('cibles.precision.col_header')}
                    </Typography>
                    <Typography variant="caption" sx={{ width: 172, flexShrink: 0, px: 0, fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                      {t('cibles.precision.col_contact')}
                    </Typography>
                  </>
                ) : (
                  cat.cols.map((col) => (
                    <Typography key={col.field} variant="caption" sx={{ width: col.width, flexShrink: 0, px: 1.5, fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                      {t(col.labelKey)}
                      {col.optional && <Box component="span" sx={{ ml: 0.5, fontWeight: 400, textTransform: 'none', opacity: 0.7 }}>{t('cibles.col_optional')}</Box>}
                      {col.field === 'placeId' && (
                        <Tooltip
                          title={
                            <Box sx={{ p: 0.5, maxWidth: 270 }}>
                              <Typography variant="caption" fontWeight={700} display="block" gutterBottom>{t('cibles.placeid.title')}</Typography>
                              <Typography variant="caption" component="div" sx={{ lineHeight: 1.6 }}>
                                <Trans
                                  i18nKey="cibles.placeid.body"
                                  components={{
                                    mono: <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.15)', px: 0.4, borderRadius: 0.5 }} />,
                                    mono2: <Box component="span" sx={{ fontFamily: 'monospace' }} />,
                                    mono3: <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} />,
                                  }}
                                />
                              </Typography>
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 11, ml: 0.4, verticalAlign: 'middle', opacity: 0.6, cursor: 'help' }} />
                        </Tooltip>
                      )}
                    </Typography>
                  ))
                )}
              </Box>
            )}

            {/* Rows */}
            {loading && displayRows.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} sx={{ color: cat.color }} />
              </Box>
            ) : displayRows.length === 0 && !showAdd ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 7, gap: 1.5 }}>
                <cat.Icon sx={{ fontSize: 36, color: alpha(cat.color, 0.22) }} />
                <Typography variant="body2" color="text.disabled">{t('cibles.empty_cat', { cat: t(cat.labelKey).toLowerCase() })}</Typography>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setShowAdd(true)}
                  sx={{ borderRadius: 2, borderColor: cat.color, color: cat.color }}>
                  {t(cat.addLabelKey)}
                </Button>
              </Box>
            ) : activeTab === 'collection' ? (
              <CollectionListView
                rows={displayRows}
                locked={tabLocked}
                marques={marques}
                tagRegistry={tagRegistry}
                onDelete={(targetId) => handleDelete(targetId, 'own', 'collection')}
                onEdit={(row) => setCollectionEditRow(row)}
              />
            ) : activeTab === 'produit' ? (
              <ProductListView
                rows={displayRows}
                locked={tabLocked}
                marques={marques}
                onDelete={(targetId) => handleDelete(targetId, 'own', 'produit')}
                onEdit={(row) => setProductEditRow(row)}
              />
            ) : activeTab === 'contenu' ? (
              <ContenuDomainView
                rows={displayRows}
                locked={tabLocked}
                marques={marques}
                onDelete={(targetId) => handleDelete(targetId, 'own', 'contenu')}
                onEdit={(row) => setContenuEditRow(row)}
              />
            ) : (
              <>
                {displayRows.map((row) => {
                  const isRootMarque =
                    activeTab === 'marque' &&
                    row.fields.some(
                      (f) =>
                        f.name === 'name' &&
                        String(f.value).toLowerCase().trim() ===
                          activeBrand?.name?.toLowerCase().trim(),
                    );
                  return (
                  <TargetTableRow
                    key={row.targetId}
                    row={row}
                    cat={cat}
                    marques={marques}
                    locked={tabLocked}
                    isRoot={isRootMarque}
                    onDelete={isRootMarque ? () => {} : () => handleDelete(row.targetId, row.ownerId, row.category)}
                    onSave={(data) => handleSave(row.targetId, data)}
                    onColorChange={activeTab === 'marque' ? async (color) => {
                      try {
                        await api.put(`/brands/${brandId}/targets/${row.targetId}`, {
                          color,
                          ownerId: row.ownerId,
                          category: row.category,
                        });
                      } catch (e) {
                        reportWriteError(e, 'cibles.errors.color');
                        throw e;
                      }
                    } : undefined}
                    tagRegistry={tagRegistry}
                    workingLang={activeBrand?.strategy?.workingLanguage ?? 'fr'}
                  />
                  );
                })}
              </>
            )}

            {/* Add dialogs — all categories use dialogs, never inline */}
            <GenericTargetDialog
              open={showAdd && cat.id !== 'etablissement' && cat.id !== 'personne' && cat.id !== 'produit'}
              cat={cat}
              marques={marques}
              onSave={async (data) => { await handleAdd(data); setShowAdd(false); }}
              onClose={() => setShowAdd(false)}
              saving={saving}
              tagRegistry={tagRegistry}
            />
            <EstablishmentAddForm open={showAdd && activeTab === 'etablissement'} marques={marques} onAdd={handleAdd} onClose={() => { setShowAdd(false); zoneHook.reset(); }} saving={saving} zoneState={zoneState} tagRegistry={tagRegistry} />
            <PersonneAddForm open={showAdd && activeTab === 'personne'} marques={marques} onAdd={handleAdd} onClose={() => setShowAdd(false)} saving={saving} tagRegistry={tagRegistry} workingLang={activeBrand?.strategy?.workingLanguage ?? 'fr'} />
            <ProductAddForm
              open={showAdd && activeTab === 'produit'}
              marques={marques}
              onSave={async (data) => { await handleAdd(data); setShowAdd(false); }}
              onClose={() => setShowAdd(false)}
              saving={saving}
              tagRegistry={tagRegistry}
            />
            {/* Product edit dialog */}
            {productEditRow && (
              <ProductAddForm
                open={!!productEditRow}
                mode="edit"
                initialRow={productEditRow}
                marques={marques}
                onSave={async (data) => { await handleSave(productEditRow.targetId, data); setProductEditRow(null); }}
                onClose={() => setProductEditRow(null)}
                saving={saving}
                tagRegistry={tagRegistry}
              />
            )}
            {/* Collection edit dialog */}
            {collectionEditRow && (
              <GenericTargetDialog
                open={!!collectionEditRow}
                mode="edit"
                cat={CAT_BY_ID['collection']}
                initialRow={collectionEditRow}
                marques={marques}
                onSave={async (data) => { await handleSave(collectionEditRow.targetId, data); setCollectionEditRow(null); }}
                onClose={() => setCollectionEditRow(null)}
                saving={saving}
                tagRegistry={tagRegistry}
              />
            )}
            {/* Contenu edit dialog */}
            {contenuEditRow && (
              <GenericTargetDialog
                open={!!contenuEditRow}
                mode="edit"
                cat={CAT_BY_ID['contenu']}
                initialRow={contenuEditRow}
                marques={marques}
                onSave={async (data) => { await handleSave(contenuEditRow.targetId, data); setContenuEditRow(null); }}
                onClose={() => setContenuEditRow(null)}
                saving={saving}
                tagRegistry={tagRegistry}
              />
            )}

            {/* Paginator */}
            {!searchMode && displayRows.length > 0 && (
              <Box sx={{ px: 2.5, py: 1, borderTop: '1px solid', borderColor: alpha('#000', 0.06), display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  disabled={page === 0 || loading}
                  onClick={() => loadPage(page - 1, pageCursors)}
                  sx={{ color: 'text.secondary' }}
                >
                  <ArrowBackIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', minWidth: 60, textAlign: 'center' }}>
                  {loading ? <CircularProgress size={12} /> : (() => {
                    const total = catCount(manifest, activeTab);
                    const pageSize = activeTab === 'produit' ? 10 : activeTab === 'contenu' ? 200 : 50;
                    const totalPages = total > 0 ? Math.ceil(total / pageSize) : null;
                    return totalPages ? `${page + 1} / ${totalPages}` : t('cibles.page_short', { page: page + 1 });
                  })()}
                </Typography>
                <IconButton
                  size="small"
                  disabled={!hasNextPage || loading}
                  onClick={() => loadPage(page + 1, pageCursors)}
                  sx={{ color: 'text.secondary' }}
                >
                  <ArrowBackIcon sx={{ fontSize: 15, transform: 'rotate(180deg)' }} />
                </IconButton>
              </Box>
            )}

            {/* + Add button (when there are rows) */}
            {!showAdd && !searchMode && displayRows.length > 0 && (
              <Box sx={{ px: 2.5, py: 1.25, borderTop: '1px solid', borderColor: alpha('#000', 0.06) }}>
                <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: 15 }} />} onClick={() => setShowAdd(true)} disabled={tabLocked}
                  sx={{ fontSize: '0.8125rem', color: cat.color, '&:hover': { bgcolor: cat.tint } }}>
                  {t(cat.addLabelKey)}
                </Button>
              </Box>
            )}
        </>
      </Box>

      {/* ── Import footer ── */}
      <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: alpha('#000', 0.07), display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
          {t('cibles.footer.import_label', { cat: t(cat.labelKey).toLowerCase() })}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* Import is gated on importLocked only: commitImport is exempt from the geocode
            lock server-side, so greying it out during a geocode sweep would be stricter
            than the API. */}
        <input ref={fileRef} type="file" accept=".csv,.ndjson,.ndjson.gz" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f && !importLocked) handleImport(f); }} />
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const lang = (i18n.language ?? 'fr').slice(0, 2);
            const template = cat.csvTemplates[lang] ?? cat.csvTemplates['fr'];
            downloadCsv(`template-${cat.id}.csv`, template);
          }}
          startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
          sx={{ borderRadius: 1.5, fontSize: '0.75rem', color: 'text.secondary', borderColor: alpha('#000', 0.18) }}
        >
          {t('cibles.footer.template')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => fileRef.current?.click()}
          disabled={importLocked || saving}
          sx={{ borderRadius: 1.5, fontSize: '0.75rem', borderColor: cat.color, color: cat.color }}
        >
          {t('cibles.footer.import')}
        </Button>
        {/* Sitemap import — produit/collection only */}
        {(activeTab === 'produit' || activeTab === 'collection') && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSitemapDialogOpen(true)}
            disabled={importLocked || saving}
            sx={{ borderRadius: 1.5, fontSize: '0.75rem', borderColor: cat.color, color: cat.color }}
          >
            {t('cibles.footer.import_sitemap')}
          </Button>
        )}
        {/* Tout supprimer — for any category with rows */}
        {totalCount > 0 && catCount(manifest, activeTab) > 0 && (
          <Button
            variant="text"
            size="small"
            disabled={tabLocked || saving}
            onClick={async () => {
              if (!window.confirm(t('cibles.footer.delete_all_confirm', { cat: t(cat.labelKey), interpolation: { escapeValue: false } }))) return;
              setSaving(true);
              try {
                await api.delete(`/brands/${brandId}/targets?category=${activeTab}&ownerId=own`);
                setRows([]); loadRows(); loadSummary();
              } catch (e: any) { reportWriteError(e); }
              finally { setSaving(false); }
            }}
            sx={{ fontSize: '0.75rem', color: 'error.main', '&:hover': { bgcolor: alpha('#EF4444', 0.07) } }}
          >
            {t('cibles.footer.delete_all')}
          </Button>
        )}
      </Box>

      {/* Sitemap import dialog — produit/collection only */}
      <SitemapImportDialog
        open={sitemapDialogOpen}
        brandId={brandId}
        marques={marques}
        importLocked={importLocked}
        onClose={() => setSitemapDialogOpen(false)}
        onSuccess={() => { loadRows(); loadSummary(); }}
      />
    </Box>
  );
}
